<template>

	<div v-if="keywordError" class="container-fluid px-0">
		<NavButtons light-btns></NavButtons>
		<div class="d-md-none d-flex align-items-center justify-content-between small-nav">
			<a v-load-page="pageSettings.previousPage" v-ripple
			   class="navbar-brand fw-bold fs-4 py-0 m-0 rounded-circle">
				<i class="hoverable-icon bi-arrow-left"></i>
			</a>
		</div>
		<div class="container-fluid px-0 text-center">
			<h3>Oops! It seems like this page does not exist.</h3>
			<p>But no worries, we have many more pages and medication reviews to be explored.</p>
			<p>Please check the URL or navigate back to the home page.</p>

			<RouterLink to="/">Go back to the Explore Meds homepage</RouterLink>
		</div>
	</div>

	<div v-else class="container-fluid px-0">
		<!-- Nav (Large screens) -->
		<NavButtons light-btns></NavButtons>

		<!-- Title  (Large screens) -->
		<h2 class="d-none d-md-block fw-bold text-start fs-22">{{ pageSettings.title }}</h2>

		<!-- Nav (Small screens) -->
		<div class="d-md-none d-flex align-items-center justify-content-between small-nav">
			<!-- Back icon -->
			<a v-load-page="pageSettings.previousPage" v-ripple
			   class="navbar-brand fw-bold fs-4 py-0 m-0 rounded-circle">
				<i class="hoverable-icon bi-arrow-left"></i>
			</a>
			<!-- Title -->
			<p class="fs-5 fw-bold m-0">{{ pageSettings.title }}</p>
			<!-- Menu toggler -->
			<i class="hoverable-icon bi-three-dots-vertical" v-ripple></i>
		</div>
	</div>

	<Filters v-if="!keywordError" ref="filterRef" :filter-options="filterOptions" @filters-updated="(value) => filtersUpdated(value)" :sort="defaultSort"
	         :sort_dir="defaultSortDir"></Filters>

	<CardsContainer v-if="!keywordError" :url="'/'+keyword" :filters="selectedFilters" :parent="keyword" :page="'basic'"/>


</template>

<script setup>
import { computed, ref, watch } from 'vue';
import NavButtons from '@/components/NavButtons.vue';
import Filters from '@/components/Table/Filters.vue';
import CardsContainer from '@/components/Card/CardsContainer.vue';

const props = defineProps({
	keyword: String
});

const filterRef = ref(null);

const pageTitle = ref({
	medications: 'Medications',
	categories: 'Categories',
	conditions: 'Conditions',
	symptoms: 'Symptoms',
	therapies: 'Therapies',
	side_effects: 'Side Effects'
});

const defaultSort = ref('usage_count');
const defaultSortDir = ref('desc');
const selectedFilters = ref({sort: 'usage_count', dir: 'desc'});
const keywordError = ref(false);

const filterOptions = computed(() => {
	switch(props.keyword) {
		case 'medications':
			return [
				{label: "Has been Surveyed", value: 'true', type: 'selection', filterOn: 'has_been_surveyed', selected: null},
				{
					label: 'Popularity',
					type: 'popup',
					options: [
						{label: 'Popularity', value: 'usage_count', dir: 'desc'},
						{label: 'Name', value: 'name', dir: 'asc'},
						{label: 'Rating', value: 'rating', dir: 'desc'}
					],
					forSort: true
				}
			];
		case 'categories':
			return [
				{
					label: 'Popularity',
					type: 'popup',
					options: [
						{label: 'Popularity', value: 'usage_count', dir: 'desc'},
						{label: 'Name', value: 'name', dir: 'asc'},
						{label: 'Rating', value: 'rating', dir: 'desc'}
					],
					forSort: true
				}
			];
		case 'conditions':
			return [
				// {label: "Filter", value: 'single_value_filter', type: 'selection', filterOn: 'conditions', selected: null},
				{
					label: 'Popularity',
					type: 'popup',
					options: [
						{label: 'Popularity', value: 'usage_count', dir: 'desc'},
						{label: 'Name', value: 'name', dir: 'asc'},
						{label: 'Rating', value: 'rating', dir: 'desc'}
					],
					forSort: true
				}
			];
		case 'symptoms':
			return [
				// {label: "Filter", value: 'single_value_filter', type: 'selection', filterOn: 'symptoms', selected: null},
				{
					label: 'Popularity',
					type: 'popup',
					options: [
						{label: 'Popularity', value: 'frequency', dir: 'desc'},
						{label: 'Name', value: 'name', dir: 'asc'},
						{label: 'Severity', value: 'severity', dir: 'desc'}
					],
					forSort: true
				}
			];
		case 'therapies':
			return [
				// {label: "Filter", value: 'single_value_filter', type: 'selection', filterOn: 'therapies', selected: null},
				{
					label: 'Popularity',
					type: 'popup',
					options: [
						{label: 'Popularity', value: 'usage_count', dir: 'desc'},
						{label: 'Name', value: 'name', dir: 'asc'},
						{label: 'Rating', value: 'rating', dir: 'desc'}
					],
					forSort: true
				}
			];
		case 'side_effects':
			return [
				// {label: "Filter", value: 'single_value_filter', type: 'selection', filterOn: 'side_effects', selected: null},
				{
					label: 'Popularity',
					type: 'popup',
					options: [
						{label: 'Popularity', value: 'frequency', dir: 'desc'},
						{label: 'Name', value: 'name', dir: 'asc'},
						{label: 'Rating', value: 'severity', dir: 'desc'}
					],
					forSort: true
				}
			];
		default:
			return [];
	}
})

const pageSettings = computed(() => {
	return {
		title: pageTitle.value[props.keyword] || 'Basic Page',
		breadCrumbs: [
			{link: '/', name: 'Home', active: false},
			{link: '/' + props.keyword, name: pageTitle.value[props.keyword], active: true}
		],
		previousPage: '/'
	};
});

const setDefaults = () => {
	switch(props.keyword) {
		case 'medications':
			defaultSort.value = 'usage_count';
			defaultSortDir.value = 'desc';
			selectedFilters.value = {sort: 'usage_count', dir: 'desc'};
			break;
		case 'categories':
			defaultSort.value = 'usage_count';
			defaultSortDir.value = 'desc';
			selectedFilters.value = {sort: 'usage_count', dir: 'desc'};
			break;
		case 'conditions':
			defaultSort.value = 'usage_count';
			defaultSortDir.value = 'desc';
			selectedFilters.value = {sort: 'usage_count', dir: 'desc'};
			break;
		case 'symptoms':
			defaultSort.value = 'frequency';
			defaultSortDir.value = 'desc';
			selectedFilters.value = {sort: 'frequency', dir: 'desc'};
			break;
		case 'therapies':
			defaultSort.value = 'usage_count';
			defaultSortDir.value = 'desc';
			selectedFilters.value = {sort: 'usage_count', dir: 'desc'};
			break;
		case 'side_effects':
			defaultSort.value = 'frequency';
			defaultSortDir.value = 'desc';
			selectedFilters.value = {sort: 'frequency', dir: 'desc'};
			break;
		default:
			defaultSort.value = '';
			defaultSortDir.value = ''
			selectedFilters.value = {};
	}
}

const filtersUpdated = (filters) => {
	selectedFilters.value = filters;
}

watch(() => props.keyword, (newKeyword) => {
	if(newKeyword) {
		if(filterRef.value) {
			filterRef.value.resetFilters();
			setDefaults();
		}
		else{
			setDefaults();
		}
		keywordError.value = !pageTitle.value[newKeyword];
	}
}, {immediate: true});

</script>

<style lang="scss" scoped>
.fs-22 {
	font-size: 2.2rem;
}
</style>